import React, { useEffect, useState } from 'react';
import { Table, Switch, Card, Row, Col, Typography, message, Tag, Tabs } from 'antd';
import { httpGet, httpPut, customBaseUrl } from '../../../services/http';
import { showLoader, hideLoader } from '../../../utils/loader';

const { Text } = Typography;
const { TabPane } = Tabs;

const SmsProvider = () => {
    const [availableProviders, setAvailableProviders] = useState([]);
    const [activeProvider, setActiveProvider] = useState(null);
    const [providerConfigs, setProviderConfigs] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [availableRes, activeRes, configsRes] = await Promise.all([
                httpGet('/api/v1/admin/sms-provider/available', customBaseUrl.communicationUrl),
                httpGet('/api/v1/admin/sms-provider', customBaseUrl.communicationUrl),
                httpGet('/api/v1/admin/sms-provider/configs', customBaseUrl.communicationUrl)
            ]);

            // availableRes: ["twilio", "infobip", "africas-talking"]
            if (availableRes && Array.isArray(availableRes)) {
                setAvailableProviders(availableRes);
            } else if (availableRes && availableRes.status) {
                setAvailableProviders(availableRes.data || []);
            }

            // activeRes: { "id": 2, "providerName": "africas-talking", ... }
            if (activeRes && activeRes.providerName) {
                setActiveProvider(activeRes);
            } else if (activeRes && activeRes.status) {
                setActiveProvider(activeRes.data);
            }

            // configsRes: [{ "id": 2, "providerName": "africas-talking", ... }, ...]
            if (configsRes && Array.isArray(configsRes)) {
                setProviderConfigs(configsRes);
            } else if (configsRes && configsRes.status) {
                setProviderConfigs(configsRes.data || []);
            }
        } catch (error) {
            message.error("Failed to fetch SMS provider data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSwitchProvider = async (providerName) => {
        showLoader();
        const userEmail = localStorage.getItem('email');
        try {
            const res = await httpPut(
                `/api/v1/admin/sms-provider/switch?provider=${providerName}&changedBy=${encodeURIComponent(userEmail)}&reason=switch`,
                {},
                customBaseUrl.communicationUrl
            );
            // res: { "changedBy": "admin", "activeProvider": "twilio", "message": "SMS provider switched successfully", ... }
            if (res && (res.status || res.message?.toLowerCase().includes('success') || res === true)) {
                message.success(res.message || `Successfully switched to ${providerName}`);
                fetchData();
            } else {
                message.error(res?.message || "Failed to switch provider");
            }
        } catch (error) {
            message.error("An error occurred while switching provider");
        } finally {
            hideLoader();
        }
    };

    const columns = [
        {
            title: 'Provider Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text ? text.toUpperCase() : 'N/A'}</Text>
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const isActive = activeProvider && activeProvider.providerName === record.name;
                return (
                    <Tag color={isActive ? 'green' : 'default'}>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                    </Tag>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => {
                const isActive = activeProvider && activeProvider.providerName === record.name;
                return (
                    <Switch
                        checked={isActive}
                        onChange={() => !isActive && handleSwitchProvider(record.name)}
                        disabled={isActive}
                        loading={loading}
                    />
                );
            }
        }
    ];

    const configColumns = [
        {
            title: 'Provider',
            dataIndex: 'providerName',
            key: 'providerName',
            render: (text) => <Text strong>{text ? text.toUpperCase() : 'N/A'}</Text>
        },
        {
            title: 'Reason',
            dataIndex: 'reason',
            key: 'reason',
            render: (text) => text || 'N/A'
        },
        {
            title: 'Changed By',
            dataIndex: 'changedBy',
            key: 'changedBy',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Timestamp',
            dataIndex: 'changedAt',
            key: 'changedAt',
            render: (text) => text ? new Date(text).toLocaleString() : 'N/A'
        },
        {
            title: 'Active Status',
            dataIndex: 'active',
            key: 'active',
            render: (active) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'ENABLED' : 'DISABLED'}
                </Tag>
            )
        }
    ];

    return (
        <Card title="SMS Provider Management" bordered={false} className="shadow-sm">
            <Row gutter={[16, 24]}>
                <Col span={24}>
                    <Tabs defaultActiveKey="1">
                        <TabPane tab="Available Providers" key="1">
                            <Table
                                dataSource={availableProviders.map(p => ({ key: p, name: p }))}
                                columns={columns}
                                pagination={false}
                                loading={loading}
                            />
                        </TabPane>
                        <TabPane tab="Provider Configs/Logs" key="2">
                            <Table
                                dataSource={providerConfigs.map((c) => ({ ...c, key: c.id }))}
                                columns={configColumns}
                                pagination={true}
                                loading={loading}
                            />
                        </TabPane>
                    </Tabs>
                </Col>
            </Row>
        </Card>
    );
};

export default SmsProvider;
